import { decodeToken } from 'react-jwt';

import type { EvoCentralInstances } from '../../../apiClients/discoveryClient/requests/Discovery.types';
import type {
    CentralInstance,
    CentralInstances,
} from '../../../apiClients/entitlementsClient/requests/Entitlements.types';
import type { LicenseToken, LoadingHookUser } from '../../../apiClients/types';
import {
    COMBINED_TOKEN_KEY,
    LAST_USED_INSTANCE_HUB_CODE_STORAGE_KEY,
    LAST_USED_INSTANCE_STORAGE_KEY,
} from '../../../constants';
import { jwtDoesNotContainText } from '../../../util/decodeJwt';
import { getSelectedInstanceState } from '../selectedInstanceHelper';

export type Org = { id: string; display_name: string };
export type Hub = {
    code: string;
    display_name: string;
    url: string;
    entitlements?: Record<string, string>; // Workspaces extension for multi hub auth, eg {file: 'eyJxxx'}
};

export type EvoInstance = {
    hub?: Hub;
    org?: Org;
    hasNoLicense?: boolean;
};

export type EvoInstances = Array<{ hub: Hub; org: Org }>;

export type DecodedToken = {
    given_name: string;
    family_name: string;
    email: string;
    sub: string;
    role?: string[];
    [key: string]: unknown;
};

export type OrgInstanceLicenseAccess = {
    instance_id?: string;
    instance_name?: string;
    org_id?: string;
    org_name?: string;
    roles?: string[];
};

export const CURRENT_EVO_INSTANCE_KEY = 'evo';
export const LICENSE_TOKEN_KEY = 'token';
export const ALL_EVO_INSTANCES_KEY = 'allEvo';
export const ALL_CENTRAL_INSTANCES_KEY = 'allCentral';
export const ALL_EVO_CENTRAL_INSTANCES_KEY = 'allEvoCentral';
export const FAKE_LOCAL_BMS_INSTANCE_CODE = 'local-bms';
// Keep the legacy stored value so existing browser selections remain valid.
export const FAKE_CUSTOM_GCP_SERVICES_INSTANCE_CODE = 'custom-gcp-bms';
/** @deprecated Use FAKE_CUSTOM_GCP_SERVICES_INSTANCE_CODE. */
export const FAKE_CUSTOM_GCP_BMS_INSTANCE_CODE = FAKE_CUSTOM_GCP_SERVICES_INSTANCE_CODE;
export const INSTANCE_OVERRIDE_IDS = ['local-bms'] as const;
export type InstanceOverrideId = (typeof INSTANCE_OVERRIDE_IDS)[number];
export const INSTANCE_OVERRIDE_CODES = [
    FAKE_LOCAL_BMS_INSTANCE_CODE,
    FAKE_CUSTOM_GCP_SERVICES_INSTANCE_CODE,
];
// Deployed instances which can be used as the source for development overrides.
export const INSTANCE_OVERRIDE_DEV_HUB_CODES = ['263bms', 'dau'];
export const EVO_INSTANCE_USER_ROLE = 'evoUserRole';
export const ORG_INSTANCE_ACCESS = 'orgInstanceAccess';
export const EVO_INSTANCES_CHANGED_EVENT = 'evoInstancesChanged';

function notifyEvoInstancesChanged(): void {
    window.dispatchEvent(new Event(EVO_INSTANCES_CHANGED_EVENT));
}

export function getCurrentUserDetails(): LoadingHookUser | undefined {
    const accessToken = JSON.parse(localStorage.getItem(COMBINED_TOKEN_KEY) || '{}');
    const decodedToken: DecodedToken | null = decodeToken(accessToken?.access_token);

    if (decodedToken) {
        const userDetails = {
            data: {
                given_name: decodedToken?.given_name,
                family_name: decodedToken?.family_name,
                email: decodedToken?.email,
                sub: decodedToken?.sub,
            },
            isLoading: false,
            isFetching: false,
            isError: false,
            error: null,
        };
        return userDetails;
    }

    return undefined;
}

export function getLastUsedInstance() {
    const lastUsedInstanceId: string | undefined =
        localStorage.getItem(LAST_USED_INSTANCE_STORAGE_KEY) || undefined;
    const lastUsedHubCode =
        localStorage.getItem(LAST_USED_INSTANCE_HUB_CODE_STORAGE_KEY) || undefined;

    const lastUsedEntitlement = getAvailableEvoInstances().find(
        ({ hub, org }) =>
            org.id === lastUsedInstanceId && (!lastUsedHubCode || hub.code === lastUsedHubCode),
    );

    // the lastUsedInstanceID isn't recognised and should be removed
    if (lastUsedInstanceId && !lastUsedEntitlement) {
        localStorage.removeItem(LAST_USED_INSTANCE_STORAGE_KEY);
        return undefined;
    }

    return lastUsedEntitlement;
}

export function setLastUsedInstance(instanceId?: string, hubCode?: string) {
    if (instanceId) {
        localStorage.setItem(LAST_USED_INSTANCE_STORAGE_KEY, instanceId);
    }
    if (hubCode) {
        localStorage.setItem(LAST_USED_INSTANCE_HUB_CODE_STORAGE_KEY, hubCode);
    } else {
        localStorage.removeItem(LAST_USED_INSTANCE_HUB_CODE_STORAGE_KEY);
    }
}

export function removeLastUsedInstance() {
    localStorage.removeItem(LAST_USED_INSTANCE_STORAGE_KEY);
    localStorage.removeItem(LAST_USED_INSTANCE_HUB_CODE_STORAGE_KEY);
}

export function setCurrentEvoInstance(entitlements: EvoInstance): void {
    sessionStorage.setItem(CURRENT_EVO_INSTANCE_KEY, JSON.stringify(entitlements));
    notifyEvoInstancesChanged();
}

export function getCurrentEvoInstance(): EvoInstance {
    try {
        return JSON.parse(sessionStorage.getItem(CURRENT_EVO_INSTANCE_KEY) || '{}');
    } catch (_error) {
        return {};
    }
}

export function setAvailableEvoInstances(instances: EvoInstances): void {
    localStorage.setItem(ALL_EVO_INSTANCES_KEY, JSON.stringify(instances));
    notifyEvoInstancesChanged();
}

function addLocalBmsInstance(instances: EvoInstances): EvoInstances {
    const sourceInstance = instances.find(({ hub }) =>
        INSTANCE_OVERRIDE_DEV_HUB_CODES.includes(hub.code),
    );
    if (!sourceInstance) return instances;

    return [
        ...instances,
        {
            hub: {
                code: FAKE_LOCAL_BMS_INSTANCE_CODE,
                display_name: 'Local BMS',
                url: `${window.location.origin}/local-mock-services`,
            },
            org: {
                ...sourceInstance.org,
                display_name: '(Local BMS)',
            },
        },
    ];
}

const INSTANCE_OVERRIDE_FACTORIES: Record<
    InstanceOverrideId,
    (instances: EvoInstances) => EvoInstances
> = {
    'local-bms': addLocalBmsInstance,
};

// Optional summary appended to the Custom GCP services display name. The consuming app sets it
// at startup so the login library does not need access to app-level service configuration.
let customGcpServicesDisplayLabel = '';
let customGcpServicesEnabled = false;
let enabledInstanceOverrideIds: readonly InstanceOverrideId[] = [];

export function setCustomGcpServicesDisplayLabel(label: string): void {
    customGcpServicesDisplayLabel = label;
}

export function setCustomGcpServicesEnabled(enabled: boolean): void {
    customGcpServicesEnabled = enabled;
}

export function setEnabledInstanceOverrideIds(ids: readonly InstanceOverrideId[]): void {
    enabledInstanceOverrideIds = ids;
}

export function isInstanceOverrideId(value: string): value is InstanceOverrideId {
    return INSTANCE_OVERRIDE_IDS.some((id) => id === value);
}

export function isInstanceOverrideCode(code: string): boolean {
    return INSTANCE_OVERRIDE_CODES.includes(code);
}

/** @deprecated Use setCustomGcpServicesDisplayLabel. */
export function setCustomGcpBmsDisplayLabel(label: string): void {
    setCustomGcpServicesDisplayLabel(label);
}

/** @deprecated Use setCustomGcpServicesEnabled. */
export function setCustomGcpBmsEnabled(enabled: boolean): void {
    setCustomGcpServicesEnabled(enabled);
}

function addCustomGcpServicesInstance(instances: EvoInstances): EvoInstances {
    const labelSuffix = customGcpServicesDisplayLabel ? ` (${customGcpServicesDisplayLabel})` : '';
    const currentInstance = getCurrentEvoInstance();
    const eligibleInstances = instances.filter(({ hub }) =>
        INSTANCE_OVERRIDE_DEV_HUB_CODES.includes(hub.code),
    );
    const sourceInstance =
        eligibleInstances.find(
            ({ hub, org }) =>
                org.id === currentInstance.org?.id &&
                (hub.code === currentInstance.hub?.code || hub.url === currentInstance.hub?.url),
        ) ?? eligibleInstances[0];

    if (!sourceInstance) return instances;

    return [
        ...instances,
        {
            hub: {
                code: FAKE_CUSTOM_GCP_SERVICES_INSTANCE_CODE,
                display_name: 'Custom GCP services',
                url: sourceInstance.hub.url,
            },
            org: {
                ...sourceInstance.org,
                display_name: `(Custom GCP services)${labelSuffix}`,
            },
        },
    ];
}

export function getAvailableEvoInstances(): EvoInstances {
    try {
        let instances: EvoInstances = JSON.parse(
            localStorage.getItem(ALL_EVO_INSTANCES_KEY) || '[]',
        );
        if (
            instances.length >= 1 &&
            (enabledInstanceOverrideIds.length || customGcpServicesEnabled)
        ) {
            instances = enabledInstanceOverrideIds.reduce(
                (availableInstances, id) => INSTANCE_OVERRIDE_FACTORIES[id](availableInstances),
                instances,
            );
            if (customGcpServicesEnabled) {
                instances = addCustomGcpServicesInstance(instances);
            }
        }

        return instances;
    } catch (_error) {
        return [];
    }
}

export function setLicenseToken(jwtToken: string): void {
    // check & remove any invalid tokens (in the case of a normal user, they will still get back an admin token, but it will be invalid)
    const filteredToken = jwtToken
        .split('&')
        .filter((token) => jwtDoesNotContainText(token, 'denied'))
        .join('&');
    sessionStorage.setItem(LICENSE_TOKEN_KEY, filteredToken);
}

export function getLicenseToken(): string | null {
    return sessionStorage.getItem(LICENSE_TOKEN_KEY);
}

export function getAdminFromToken(): boolean {
    const license = getLicenseToken();
    const decodedToken = decodeToken<LicenseToken>(license || '');
    return !!decodedToken?.is_admin;
}

export function setCentralInstances(instances: CentralInstances): void {
    localStorage.setItem(ALL_CENTRAL_INSTANCES_KEY, JSON.stringify(instances));
}

export function getCentralInstances(): CentralInstances {
    try {
        return JSON.parse(localStorage.getItem(ALL_CENTRAL_INSTANCES_KEY) || '{"servers": []}');
    } catch (_error) {
        return { servers: [] };
    }
}

export function setEvoCentralInstances(instances: EvoCentralInstances): void {
    localStorage.setItem(ALL_EVO_CENTRAL_INSTANCES_KEY, JSON.stringify(instances));
}

export function getEvoCentralInstances(): EvoCentralInstances {
    try {
        return JSON.parse(
            localStorage.getItem(ALL_EVO_CENTRAL_INSTANCES_KEY) || '{"instances": []}',
        );
    } catch (_error) {
        return { instances: [] };
    }
}

export function getCurrentCentralInstance(): CentralInstance | undefined {
    const selectedInstanceState = getSelectedInstanceState();
    const centralInstances = getCentralInstances();
    const centralInstance = centralInstances.servers.find(
        (server: CentralInstance) => server.host === selectedInstanceState,
    );
    if (centralInstance) {
        return centralInstance;
    }

    const discoveryInstance = getEvoCentralInstances().instances.find(
        (instance) => instance.centralHost === selectedInstanceState,
    );
    if (!discoveryInstance) {
        return undefined;
    }

    return {
        displayName: discoveryInstance.centralDisplayName,
        host: discoveryInstance.centralHost,
        port: '443',
        instanceUUID: discoveryInstance.centralInstanceUUID,
        entitlementId: '',
        name: discoveryInstance.centralName,
        organizationName: discoveryInstance.centralOrganizationName,
        validTo: '',
    };
}

export function getRoles(): string[] | null {
    const access = getOrgInstanceAccess();

    if (access == null) {
        return null;
    }

    return access.roles || [];
}

export function setOrgInstanceAccess(licenseAccess: OrgInstanceLicenseAccess): void {
    sessionStorage.setItem(ORG_INSTANCE_ACCESS, JSON.stringify(licenseAccess));
}

export function getOrgInstanceAccess(): OrgInstanceLicenseAccess | null {
    const item = sessionStorage.getItem(ORG_INSTANCE_ACCESS);

    if (item == null) {
        return null;
    }

    try {
        return JSON.parse(sessionStorage.getItem(ORG_INSTANCE_ACCESS) || '{}');
    } catch (_error) {
        return {};
    }
}

export function getCurrentOrgId(): string {
    return getCurrentEvoInstance()?.org?.id || '';
}

export function clearEntitlements(): void {
    sessionStorage.removeItem(CURRENT_EVO_INSTANCE_KEY);
    sessionStorage.removeItem(LICENSE_TOKEN_KEY);
    sessionStorage.removeItem(ORG_INSTANCE_ACCESS);
    localStorage.removeItem(ALL_EVO_INSTANCES_KEY);
    localStorage.removeItem(ALL_CENTRAL_INSTANCES_KEY);
    localStorage.removeItem(ALL_EVO_CENTRAL_INSTANCES_KEY);
}
